import React, { useState, useContext, useMemo, useEffect } from "react";
import axios from "axios";
import { Row, Col } from "react-bootstrap";
import CopayAdjustmentManagerForm from "./CopayAdjustmentManagerForm";
import Accordion from "../../Accordion";
import { LoadingSpinner } from "./../../layout/LoadingSpinner";
import ErrorHandler from "./../../core/ErrorHandler";
import { GridColumn } from "@progress/kendo-react-grid";
import {
  MoneyCell,
  AggregateMoneyFooterCell,
} from "../../grid/Kendo/CustomKendoGridCells";
import SharedMainSelectableClientGrid from "shared/components/grids/clientGrid/SharedMainSelectableClientGrid";
import { UserContext } from "shared/context/UserContext";
import {
  Base64ToArrayBuffer,
  SaveByteArray,
} from "shared/helpers/Base64ArrayTranslator";
import {
  DefaultColumnWidth,
  DefaultDateColumnWidth,
} from "../../grid/Kendo/KendoGridAndColumnConstants";
import { useFetchCopayReportData } from "./hooks/useFetchCopayReportData";
import useGridDataStateResetProcessing from "shared/hooks/useGridDataStateResetProcessing";
//import { useReactQueryHelpers } from "shared/hooks/useReactQueryHelpers";

export default function CopayAdjustmentManager(props) {
  const { passedProps } = props;
  const user = passedProps.user.email;
  const userContext = useContext(UserContext);
  const [submittedFormData, setSubmittedFormData] = useState(null);
  const [error, setError] = useState({});
  const [gridAggregates, setGridAggregates] = useState([]);
  const [exportParameter, setExportParameter] = useState(null);
  let gridAggregateColumnSettings = [
    { aggregate: "sum", field: "difference" },
  ];
  //const { invalidateReactQueryKey } = useReactQueryHelpers();
  const {
    gridDataStateResetProcessingInitiated,
    InitiateGridDataStateReset,
    GridDataStateResetCompleted: onGridDataStateResetCompleted,
  } = useGridDataStateResetProcessing();



  var { data: response } =
    useFetchCopayReportData(exportParameter, onDownloadAttachmentFailure);

  useEffect(() => {
    if (response) {
      const base64Response = response.fileStream;
      const fileName = response.fileName;
      let downloadBytes = Base64ToArrayBuffer(base64Response);

      SaveByteArray(fileName, downloadBytes);

      //invalidateReactQueryKey("api/CopayAdjustmentManager/DownloadCopayReport");
      setExportParameter(null);
    }
  }, [response])

  function ExportXL() {
    var list = userContext.recLocations;
    var locationList = "";
    for (var i = 0; i < list.length; i++) {
      locationList += list[i].value + ",";
    }
    let parameters = {
      mmids: locationList,
    };
    setExportParameter(parameters);
  }
  function onDownloadAttachmentFailure() {
    let msg = `Unable to download Copay report.`;
    setExportParameter(null)
    alert(msg);
  }

  const handleExportExcel = () => {
    ExportXL();
  };

  const GridAggregateMoneyFooterCell = (props) => {
    var options = {
      aggregateData: gridAggregates,
    };

    return AggregateMoneyFooterCell(props, options);
  };

  const sendAggregatesToParent = (aggregatesFromSharedMainAgingGrid) => {
    setGridAggregates(aggregatesFromSharedMainAgingGrid.data);
  };

  function handleFormSubmit(e) {
    InitiateGridDataStateReset();
    setSubmittedFormData(e);
  }

  const clientGridApiParams = useMemo(() => {
    if (!submittedFormData) {
      return {};
    }

    return {
      user: user,
      FromDate: submittedFormData.fromDate,
      ToDate: submittedFormData.toDate,
      Locations: submittedFormData.locations,
      Payers: submittedFormData.payers,
      DateType: submittedFormData.dateType,
    };
  }, [submittedFormData]);

  return (
    <div className="feature">
      <ErrorHandler
        error={error}
        onClose={() => {
          setError({});
        }}
      />

      <Accordion defaultExpanded label="Search & Filter">
        <Row>
          <Col className="higher-zindex-filters">
            <CopayAdjustmentManagerForm
              handleFormSubmit={handleFormSubmit}
              handleExportExcel={handleExportExcel}
            />
          </Col>
        </Row>
      </Accordion>
      <Row>
        <Col className="tight-grid multi-line-filter">

          <SharedMainSelectableClientGrid
            apiGetEndpoint={"api/CopayAdjustmentManager/GetCopayAdjustmentData"}
            apiParameters={clientGridApiParams}
            aggregateColumnSettings={gridAggregateColumnSettings}
            sendAggregatesParentCallback={sendAggregatesToParent}
            styleOverride={{ minHeight: "19em", maxHeight: "50em" }}
            exportFileNamePrefix={"CoPayAdjustmentManager"}
            gridDataStateResetProcessingInitiated={
              gridDataStateResetProcessingInitiated
            }
            onGridDataStateResetCompleted={onGridDataStateResetCompleted}                         
          >
            <GridColumn
              key="NCPDP"
              field="ncpdp"
              title="NCPDP"
              width={DefaultColumnWidth()}
              locked={true}
            />
            <GridColumn
              key="PharmacyName"
              field="pharmacyName"
              title="Pharmacy Name"
              width={DefaultColumnWidth()}
              locked={true}
            />
            <GridColumn
              key="RxNumber"
              field="rxNumber"
              title="RX #"
              width={DefaultColumnWidth()}
              locked={true}
            />
            <GridColumn
              key="ServiceDate"
              field="serviceDate"
              title="Service Date"
              width={DefaultDateColumnWidth()}
              locked={true}
              filter={"date"}
              format="{0:d}"
            />
            <GridColumn
              key="TransactionDate"
              field="transactionDate"
              title="Transaction Date"
              width={DefaultDateColumnWidth()}
              locked={true}
              filter={"date"}
              format="{0:d}"
            />
            <GridColumn
              key="PatientID"
              field="patientID"
              title="Patient ID"
              width={DefaultColumnWidth()}
            />

            <GridColumn
              key="FacilityID"
              field="facilityID"
              title="Facility ID"
              width={DefaultColumnWidth()}
            />
            <GridColumn
              key="PatientName"
              field="patientName"
              title="Patient Name"
              width={DefaultColumnWidth()}
            />
            <GridColumn
              key="ClaimPBMName"
              field="claimPBMName"
              title="Claim PBM Name"
              width={DefaultColumnWidth()}
            />
            <GridColumn
              key="Bin"
              field="bin"
              title="Bin"
              width={DefaultColumnWidth()}
            />
            <GridColumn
              key="Pcn"
              field="pcn"
              title="PCN"
              width={DefaultColumnWidth()}
            />
            <GridColumn
              key="GroupID"
              field="groupID"
              title="Group ID"
              width={DefaultColumnWidth()}
            />
            <GridColumn
              key="Adjudicated"
              field="adjudicated"
              title="Adjudicated"
              filter="numeric"
              width={DefaultColumnWidth()}
              cells={{
                data: MoneyCell
              }}
            />
            <GridColumn
              key="AdjudicatedCopay"
              field="adjudicatedCopay"
              title="Adjudicated Copay"
              filter="numeric"
              width={DefaultColumnWidth()}
              cells={{
                data: MoneyCell
              }}
            />
            <GridColumn
              key="RemitPayerName"
              field="remitPayerName"
              title="Remit Payer Name"
              width={DefaultColumnWidth()}
            />
            <GridColumn
              key="CheckNumber"
              field="checkNumber"
              title="Check #"
              width={DefaultColumnWidth()}
            />
            <GridColumn
              key="CheckDate"
              field="checkDate"
              title="Check Date"
              width={DefaultDateColumnWidth()}
              filter={"date"}
              format="{0:d}"
            />
            <GridColumn
              key="Billed"
              field="billed"
              title="Billed"
              width={DefaultColumnWidth()}
              filter="numeric"
              cells={{
                data: MoneyCell
              }} />
            <GridColumn
              key="Adjusted"
              field="adjusted"
              title="Adjusted"
              width={DefaultColumnWidth()}
              filter="numeric"
              cells={{
                data: MoneyCell
              }} />
            <GridColumn
              key="RemitPaid"
              field="remitPaid"
              title="Remit Paid"
              filter="numeric"
              width={DefaultColumnWidth()}
              cells={{
                data: MoneyCell
              }} />
            <GridColumn
              key="RemitCopay"
              field="remitCopay"
              title="Remit Copay"
              width={DefaultColumnWidth()}
               filter="numeric"
              cells={{
                data: MoneyCell
              }} />
            <GridColumn
              key="CopayType"
              field="copayType"
              title="Copay Type"
              width={DefaultColumnWidth()}
            />
            <GridColumn
              key="Difference"
              field="difference"
              title="Difference"
              width={DefaultColumnWidth()}
              filter="numeric"
              cells={{
                data: MoneyCell,
                footerCell: GridAggregateMoneyFooterCell,
              }}
            />

          </SharedMainSelectableClientGrid>
        </Col>
      </Row>
    </div>
  );
}
